import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { type QueryClient } from '@tanstack/react-query';

import { useNavigateBack } from '#apps/client-web/app/hooks/use-navigate-back';
import { useLoadAuditFormByType } from '#apps/client-web/features/audit-form/hooks/use-load-audit-form-by-type-query';
import { useSaveAuditFormMutation } from '#apps/client-web/features/audit-form/hooks/use-save-audit-form-mutation';
import { type AuditFormService } from '#apps/client-web/features/audit-form/services/audit-form';
import { AUDIT_FORMS_QUERY_KEY } from '#apps/client-web/features/audit-form/types/react-query';

import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { type AuditId } from '#packages/data-collection/models/audit';
import {
  type QueryPageState,
  type MutationPageState,
} from '#packages/data-collection/types/page-state';
import {
  AuditChecklistFormSchema,
  type AuditChecklistForm,
} from '#packages/data-collection/models/audit-checklist-form';
import { omit } from '#packages/data-collection/libs/lodash';

import { type EditAuditFormChecklistData } from './types';
import { fromFormData } from './mappers';

export type ChecklistFormPageViewModel = ReturnType<
  typeof useAuditFormChecklistPageViewModel
>;

type AuditFormChecklistPageViewModelProps = LoggingContext & {
  auditFormService: AuditFormService;
  queryClient: QueryClient;
};

export const useAuditFormChecklistPageViewModel = ({
  auditFormService,
  queryClient,

  logger,
}: AuditFormChecklistPageViewModelProps) => {
  const goBack = useNavigateBack({ logger });

  const { auditId } = useParams<{
    auditId: AuditId;
  }>();

  const auditFormQuery = useLoadAuditFormByType<AuditChecklistForm>({
    auditFormService,
    auditId: auditId!,
    formType: 'checklist',
    queryClient,
  });

  const saveAuditFormMutation = useSaveAuditFormMutation<AuditChecklistForm>({
    auditFormService,
    queryClient,
    auditId: auditId!,
    formType: 'checklist',
  });

  const form = useForm<EditAuditFormChecklistData>({
    reValidateMode: 'onChange',

    defaultValues: {},
  });

  const onSave = useCallback(
    (formData: EditAuditFormChecklistData) => {
      if (loadAuditFormState.state !== 'success') {
        logger?.debug('Cannot update checklist, page is not in success state');
        return;
      }

      logger?.debug({ auditId, formData }, 'Updating checklist form data');

      // Encode existing data to raw format, or start with empty template
      const previousDataEncoded = auditFormQuery.data
        ? AuditChecklistFormSchema.encode(auditFormQuery.data)
        : { type: 'checklist' as const };

      // Decode once to validate and transform
      const auditChecklistForm = AuditChecklistFormSchema.decode({
        ...previousDataEncoded,
        ...fromFormData(formData),
      });

      saveAuditFormMutation.mutate(
        { auditId: auditId!, form: auditChecklistForm },
        {
          onSuccess: (savedForm: AuditChecklistForm) => {
            logger?.debug(
              { auditId, savedForm },
              'Checklist form updated successfully',
            );
            // invalidate audit checklist form queries
            queryClient
              .invalidateQueries({
                queryKey: [
                  AUDIT_FORMS_QUERY_KEY,
                  auditId,
                  previousDataEncoded.type,
                ],
              })
              .then(() => {
                logger?.debug('Checklist form queries invalidated');
                goBack();
              });
          },
          onError: (err) => {
            logger?.error({ err }, 'Failed to update checklist form');
            throw err;
          },
        },
      );
    },
    [
      saveAuditFormMutation.mutate,
      auditId,
      auditFormQuery.data,
      AuditChecklistFormSchema.encode,
      AuditChecklistFormSchema.decode,
    ],
  );

  const updateMutationState = useMemo<MutationPageState>(() => {
    if (saveAuditFormMutation.isIdle || saveAuditFormMutation.isSuccess) {
      return { state: 'idle' };
    }

    if (saveAuditFormMutation.isPending) {
      return { state: 'pending' };
    }

    if (saveAuditFormMutation.isError) {
      return { state: 'error', error: saveAuditFormMutation.error };
    }

    throw new Error('Unhandled mutation state');
  }, [
    saveAuditFormMutation.isIdle,
    saveAuditFormMutation.isPending,
    saveAuditFormMutation.isSuccess,
    saveAuditFormMutation.isError,
    saveAuditFormMutation.error,
  ]);

  const loadAuditFormState = useMemo<
    QueryPageState<AuditChecklistForm | null>
  >(() => {
    if (auditFormQuery.isSuccess) {
      return {
        state: 'success',
        data: auditFormQuery.data,
      };
    }

    if (auditFormQuery.isLoading || auditFormQuery.isPending) {
      return { state: 'loading' };
    }

    if (auditFormQuery.isError) {
      return {
        state: 'error',
        error: auditFormQuery.error,
      };
    }

    throw new Error('Unhandled query state');
  }, [
    auditFormQuery.data,
    auditFormQuery.isSuccess,
    auditFormQuery.isLoading,
    auditFormQuery.isPending,
    auditFormQuery.isError,
    auditFormQuery.error,
  ]);

  // sync form default values when audit details are loaded
  useEffect(() => {
    if (loadAuditFormState.state !== 'success') {
      return;
    }

    logger?.debug(
      { auditChecklistForm: auditFormQuery.data },
      'Audit checklist form default values',
    );

    // update form default values
    form.reset(omit(loadAuditFormState.data, ['type']));
  }, [auditFormQuery.data, auditFormQuery.isSuccess]);

  return {
    auditFormQuery,
    loadAuditFormState,

    form,
    onSubmit: form.handleSubmit(onSave),
    updateMutationState,

    auditId,
  };
};
